/**
 * Разбор адреса глубокой ссылки.
 *
 * Раньше маршрут определялся подстрочным поиском по всему href
 * (`/auth-callback|reset-password/.test(url.href)`), поэтому под него подходил ЛЮБОЙ
 * адрес, куда эти слова попали через query-параметр. А ветка `auth-callback` берёт из
 * адреса готовые токены сессии — то есть ссылка вида
 * `https://сайт/?x=auth-callback&access_token=<чужой>` молча логинила получателя
 * в чужой аккаунт (session fixation), после чего он привязывал к нему свои адреса
 * и заказы.
 */

export type DeepLinkRoute = 'auth-callback' | 'reset-password' | 'payment-return' | 'unsupported'

export interface ParsedDeepLink {
  route: DeepLinkRoute
  /** Ссылка открыта по родной схеме приложения (`myapp://…`), а не по http(s). */
  isNativeScheme: boolean
}

/**
 * У родной схемы (`myapp://auth-callback`) маршрут лежит в host, у обычной ссылки —
 * в pathname. Возврат с оплаты приходит обычной https-ссылкой с путём `/success`|`/fail`.
 */
export function parseDeepLink(rawUrl: string): ParsedDeepLink {
  let url: URL

  try {
    url = new URL(rawUrl)
  } catch {
    return { route: 'unsupported', isNativeScheme: false }
  }

  const isNativeScheme = !/^https?:$/.test(url.protocol)

  if (url.pathname.includes('/success') || url.pathname.includes('/fail')) {
    return { route: 'payment-return', isNativeScheme }
  }

  const segment = (isNativeScheme ? url.host : url.pathname).replaceAll(/^\/+|\/+$/g, '')

  if (segment === 'auth-callback' || segment === 'reset-password') {
    return { route: segment, isNativeScheme }
  }

  return { route: 'unsupported', isNativeScheme }
}

/**
 * Можно ли принять из этого адреса готовую сессию.
 *
 * Только родная схема: так веб-страница передаёт токены уже установленному приложению
 * (см. useDeepLinkRedirect). В вебе такого потока нет — VK и Яндекс меняют code на токены
 * сами, — поэтому там токены в адресе означают только попытку подсунуть чужую сессию.
 */
export function mayAcceptSessionFromDeepLink(parsed: ParsedDeepLink): boolean {
  return parsed.route === 'auth-callback' && parsed.isNativeScheme
}
